import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { LogOut, LayoutDashboard, Package, FolderOpen, Mail, ShoppingCart, Wrench, History, Trash2, Users, Pencil, Calculator, Boxes, TrendingUp, Ticket } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useTabBranding, ROLE_PRESETS } from '../../lib/useTabBranding';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [version, setVersion] = useState(null);
  const [me, setMe] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    axios
      .get(`${BACKEND_URL}/api/admin/revisions/current`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setVersion(res.data?.version ?? 0))
      .catch(() => setVersion(null));
    axios
      .get(`${BACKEND_URL}/api/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setMe(res.data))
      .catch(() => setMe(null));
  }, [navigate, location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname === path;

  const isAdmin = (me?.role || 'admin') === 'admin';

  // Brand the browser tab so it's instantly identifiable among other tabs.
  // Admin → red, Editor → amber.
  const role = me?.role || null;
  useTabBranding(role && ROLE_PRESETS[role] ? ROLE_PRESETS[role] : null);

  const menuItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/products', label: 'Products', icon: Package },
    { path: '/admin/projects', label: 'Projects', icon: FolderOpen },
    { path: '/admin/services', label: 'Services', icon: Wrench },
    { path: '/admin/contacts', label: 'Contact Forms', icon: Mail },
    { path: '/admin/orders', label: 'Orders', icon: ShoppingCart },
    { path: '/admin/revisions', label: 'Versions', icon: History },
    { path: '/admin/trash', label: 'Trash', icon: Trash2 },
    ...(isAdmin ? [{ path: '/admin/users', label: 'Manage Users', icon: Users }] : []),
  ];

  // "Tools" group — utility surfaces that don't fit the main content CRUD.
  // Rendered under a labelled "TOOLS" header in the sidebar.
  const toolItems = [
    { path: '/admin/studio', label: 'Studio', icon: Pencil },
    { path: '/admin/quotes', label: 'Quote AI', icon: Calculator },
    { path: '/admin/visualizer', label: '3D Visualizer', icon: Boxes },
    { path: '/admin/preinvest', label: 'Pre-Invest', icon: TrendingUp },
    { path: '/admin/lotto-gen', label: 'Lotto-Gen', icon: Ticket },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img
                src="/favicon.png"
                alt="Brainwave Architectural Projects Logo"
                className="h-10 w-10 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold" style={{ color: '#00CED1' }}>Admin Portal</h1>
                <p className="text-xs text-gray-600">Brainwave Architectural Projects</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {me && (
                <div className="hidden sm:flex flex-col items-end" data-testid="admin-current-user">
                  <span className="text-xs text-gray-500">{me.full_name || me.username}</span>
                  <Badge
                    variant="outline"
                    className="capitalize text-[10px] px-1.5 py-0"
                    style={isAdmin ? { borderColor: '#00CED1', color: '#00CED1' } : { borderColor: '#a16207', color: '#a16207' }}
                    data-testid="admin-role-badge"
                  >
                    {me.role || 'admin'}
                  </Badge>
                </div>
              )}
              <Button
                variant="outline"
                onClick={handleLogout}
                className="font-semibold"
                style={{ borderColor: '#00CED1', color: '#00CED1' }}
                data-testid="admin-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-white min-h-[calc(100vh-4rem)] shadow-sm flex flex-col">
          <nav className="p-4 space-y-2 flex-grow">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'font-bold text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  style={isActive(item.path) ? { backgroundColor: '#00CED1' } : {}}
                  data-testid={`admin-nav-${item.path.replace('/admin/', '')}`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}

            {/* Tools group */}
            <div className="pt-4 pb-1" data-testid="admin-nav-tools-group">
              <p className="px-4 text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em]">
                Tools
              </p>
            </div>
            {toolItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'font-bold text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  style={isActive(item.path) ? { backgroundColor: '#00CED1' } : {}}
                  data-testid={`admin-nav-${item.path.replace('/admin/', '')}`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Version label — bottom left of admin sidebar */}
          <Link
            to="/admin/revisions"
            className="m-4 p-3 rounded-lg border border-gray-200 hover:border-teal-300 hover:bg-gray-50 transition-colors"
            data-testid="admin-version-label"
            title="View version history"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 font-medium">Content version</span>
              <History className="h-3.5 w-3.5 text-gray-400" />
            </div>
            <div className="text-lg font-bold mt-0.5" style={{ color: '#00CED1' }}>
              {version === null ? '…' : `v${version}`}
            </div>
          </Link>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">{children}</div>
      </div>
    </div>
  );
};

export default AdminLayout;
